/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { createContext, useContext, useMemo } from 'react';
import type { Base2Features, Base2FeaturesConfig } from './types';

/**
 * React context that BaseProvider reads to determine which Base 2.0 feature
 * sets are active. Intended to be populated by a Fusion plugin that reads
 * request headers, so that apps can be opted in without touching the component
 * tree directly.
 *
 * Prefer reading via the `useBase2Features` hook, which returns a stable,
 * normalized `{ colors, elevation, material, cornerRadius, typography }` shape regardless
 * of how the provider was configured. Prefer providing via `Base2FeaturesProvider`,
 * which memoizes the context value so callers may pass a fresh object
 * literal on every render without invalidating downstream consumers.
 *
 * @example — Fusion plugin sets it from headers; components read via the hook:
 *   // plugin wraps ctx.element:
 *   ctx.element = (
 *     <Base2FeaturesProvider value={{ colors: true }}>
 *       {ctx.element}
 *     </Base2FeaturesProvider>
 *   );
 *
 *   // anywhere inside:
 *   const { colors } = useBase2Features();
 */
export const Base2FeaturesContext = createContext<Base2FeaturesConfig | undefined>(undefined);
Base2FeaturesContext.displayName = 'Base2FeaturesContext';
const EMPTY_BASE2_FEATURES: Base2Features = Object.freeze({
  colors: false,
  elevation: false,
  material: false,
  cornerRadius: false,
  typography: false,
  cssTheming: false
});
const ALL_BASE2_FEATURES: Base2Features = Object.freeze({
  colors: true,
  elevation: true,
  material: true,
  cornerRadius: true,
  typography: true,
  cssTheming: false // 'all' enables feature flags; CSS theming remains an explicit opt-in
});
const normalizeBase2Features = (raw: Base2FeaturesConfig | undefined): Base2Features => {
  if (raw === undefined) return EMPTY_BASE2_FEATURES;
  if (raw === 'all') return ALL_BASE2_FEATURES;
  return {
    colors: raw.colors === true,
    elevation: raw.elevation === true,
    material: raw.material === true,
    cornerRadius: raw.cornerRadius === true,
    typography: raw.typography === true,
    cssTheming: raw.cssTheming === true
  };
};

/**
 * Reads the active Base 2.0 features from context. Returns a stable,
 * normalized shape with every flag resolved to a boolean; the returned
 * reference is preserved across renders as long as the upstream context
 * value has not changed.
 *
 * Outside of any provider, every flag is `false`.
 */
export const useBase2Features = (): Base2Features => {
  const raw = useContext(Base2FeaturesContext);
  return useMemo(() => normalizeBase2Features(raw), [raw]);
};